import { useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { CombinedPricingSuggestion } from '@/enterprise/data/usePricingSuggestion';
import { TakerMarket, OfferOptionType, OfferOptionFrequency } from '@/enterprise/data/useTakerMarkets';
import { ExchangeRates } from '@/shared/data/useExchangeRates';
import convertCurrency from '@/utils/convertCurrency';
import handleNaN from '@/utils/handleNaN';
import { getNormalizedRate, isValidRateInput } from '@/utils/validateRate';
import {
  OfferBuilderFrequency,
  OfferBuilderPricingStrategy,
  RateType,
  WorkingTakerMarket,
  TakerMarketError,
  OfferBuilderState,
  OfferBuilderAction,
  OfferBuilderLocation,
} from './OfferBuilderContext';
import type { PricingInformationMap } from './getPricingInformationForTracking';
import type { RateMap, RateInfo } from './rateHelpers';
import type { useReporting } from '@/reporting';

export const calculateDiscount = ({
  dpeWeightedAvg,
  invoiceAmount,
  isDiscount,
  rate,
}: {
  dpeWeightedAvg: number;
  invoiceAmount: number;
  isDiscount: boolean;
  rate: number;
}) => {
  if (!rate) {
    return 0;
  }

  let discount = 0;

  if (isDiscount) {
    discount = invoiceAmount * (rate / 100);
  } else {
    const aprRate = rate / 100;
    const dailyRate = aprRate / 360;
    discount = dailyRate * invoiceAmount * dpeWeightedAvg;
  }

  return discount;
};

export interface OfferSubmitStats {
  currency: string;
  totalInvoiceAmount: number;
  totalInvoiceAmountUsd: number;
  totalDiscount: number;
  totalDiscountUsd: number;
  totalEarlyPaymentAmount: number;
  totalEarlyPaymentAmountUsd: number;
  totalInvoiceCount: number;
}

export const computeAggregateSubmitStats = ({
  eligibleInvoiceAmount,
  discount,
  estimatedEarlyPaymentAmount,
  invoiceCount,
  currency,
  exchangeRates,
}: {
  eligibleInvoiceAmount: number;
  discount: number;
  estimatedEarlyPaymentAmount: number;
  invoiceCount: number;
  currency: string;
  exchangeRates: ExchangeRates;
}): OfferSubmitStats => ({
  currency,
  totalInvoiceAmount: Number(eligibleInvoiceAmount.toFixed(2)),
  totalInvoiceAmountUsd: Number(
    convertCurrency({ amount: eligibleInvoiceAmount, exchangeRates, from: currency, to: 'USD' }).toFixed(2)
  ),
  totalDiscount: Number(discount.toFixed(2)),
  totalDiscountUsd: Number(convertCurrency({ amount: discount, exchangeRates, from: currency, to: 'USD' }).toFixed(2)),
  totalEarlyPaymentAmount: Number(estimatedEarlyPaymentAmount.toFixed(2)),
  totalEarlyPaymentAmountUsd: Number(
    convertCurrency({ amount: estimatedEarlyPaymentAmount, exchangeRates, from: currency, to: 'USD' }).toFixed(2)
  ),
  totalInvoiceCount: invoiceCount,
});

export const computeMarketSubmitStats = ({
  takerMarket,
  rate,
  rateType,
  exchangeRates,
}: {
  takerMarket: Pick<
    TakerMarket,
    'eligibleInvoiceAmount' | 'eligibleDpeWeightedAvg' | 'eligibleInvoiceCount' | 'currency'
  >;
  rate: number;
  rateType: 'APR' | 'DISC';
  exchangeRates: ExchangeRates;
}): OfferSubmitStats => {
  const rawDiscount = calculateDiscount({
    dpeWeightedAvg: takerMarket.eligibleDpeWeightedAvg,
    invoiceAmount: takerMarket.eligibleInvoiceAmount,
    isDiscount: rateType === 'DISC',
    rate,
  });

  const rawEarlyPayment = takerMarket.eligibleInvoiceAmount - rawDiscount;

  return {
    currency: takerMarket.currency,
    totalInvoiceAmount: Number(takerMarket.eligibleInvoiceAmount.toFixed(2)),
    totalInvoiceAmountUsd: Number(
      convertCurrency({
        amount: takerMarket.eligibleInvoiceAmount,
        exchangeRates,
        from: takerMarket.currency,
        to: 'USD',
      }).toFixed(2)
    ),
    totalDiscount: Number(rawDiscount.toFixed(2)),
    totalDiscountUsd: Number(
      convertCurrency({ amount: rawDiscount, exchangeRates, from: takerMarket.currency, to: 'USD' }).toFixed(2)
    ),
    totalEarlyPaymentAmount: Number(rawEarlyPayment.toFixed(2)),
    totalEarlyPaymentAmountUsd: Number(
      convertCurrency({ amount: rawEarlyPayment, exchangeRates, from: takerMarket.currency, to: 'USD' }).toFixed(2)
    ),
    totalInvoiceCount: takerMarket.eligibleInvoiceCount,
  };
};

export type OfferSubmitPricingType =
  | 'NAME_YOUR_RATE'
  | 'PRICE_DISCOVERY'
  | 'EXPRESS_ACCEPT'
  | 'BENCHMARK'
  | 'FIXED'
  | 'PREFERRED'
  | 'STATIC';

/**
 * Routes and fires the correct per-market track event based on pricing type.
 * Enforces consistent base fields (isDraft, isEdit, marketUuid, supplierDivisionUuid, submissionBatchId)
 * across all offer submission paths. Pass caller-specific fields via `extras`.
 */
export const trackOfferSubmitEvent = ({
  pricingType,
  marketUuid,
  supplierDivisionUuid,
  submissionBatchId,
  isDraft,
  isEdit,
  isEnabled,
  isManuallyAwarded,
  marketStats,
  extras,
  trackEnterpriseEvent,
}: {
  pricingType: OfferSubmitPricingType;
  marketUuid: string;
  supplierDivisionUuid: string;
  submissionBatchId: string;
  isDraft: boolean;
  isEdit: boolean;
  isEnabled: boolean;
  isManuallyAwarded: boolean;
  marketStats: Partial<OfferSubmitStats>;
  extras?: Record<string, unknown>;
  trackEnterpriseEvent: ReturnType<typeof useReporting>['trackEnterpriseEvent'];
}) => {
  const isPreferred = pricingType === 'PREFERRED';

  const payload = {
    ...extras,
    ...marketStats,
    marketUuid,
    supplierDivisionUuid,
    submissionBatchId,
    isDraft,
    isEdit,
    isEnabled,
    isManuallyAwarded,
    isPreferred,
  };

  if (pricingType === 'BENCHMARK') {
    trackEnterpriseEvent('offer::variable-rate::submitted', payload);
  } else if (pricingType === 'FIXED' || pricingType === 'STATIC') {
    trackEnterpriseEvent('offer::fixed-rate::submitted', payload);
  } else {
    // NAME_YOUR_RATE, PRICE_DISCOVERY, EXPRESS_ACCEPT, PREFERRED
    trackEnterpriseEvent('offer::submitted', payload);
  }
};

export const calculateAverageApr = ({
  items,
}: {
  items: {
    dpeWeightedAvg: number;
    dpeWeightedSum: number;
    invoiceAmount: number;
    isDiscount: boolean;
    rate: number;
  }[];
}) => {
  if (!items.length) {
    return 0;
  }

  const { discountSum, eligibleApSum, eligibleDpeSum } = items.reduce(
    (acc, { dpeWeightedAvg, dpeWeightedSum, invoiceAmount, isDiscount, rate }) => ({
      discountSum:
        acc.discountSum +
        calculateDiscount({
          dpeWeightedAvg,
          invoiceAmount,
          isDiscount,
          rate,
        }),
      eligibleApSum: acc.eligibleApSum + invoiceAmount,
      eligibleDpeSum: acc.eligibleDpeSum + dpeWeightedSum,
    }),
    { discountSum: 0, eligibleApSum: 0, eligibleDpeSum: 0 }
  );

  if (eligibleApSum === 0 || eligibleDpeSum === 0) {
    return 0;
  }

  const averageApr = ((discountSum / eligibleApSum) * 360) / (eligibleDpeSum / eligibleApSum);

  return handleNaN(averageApr);
};

export const calculateBlendedDiscount = ({
  items,
}: {
  items: {
    dpeWeightedAvg: number;
    dpeWeightedSum: number;
    invoiceAmount: number;
    isDiscount: boolean;
    rate: number;
  }[];
}) => {
  if (!items.length) {
    return 0;
  }

  const { earn, invoiceAmount } = items.reduce(
    (acc, curr) => {
      // handle apr
      if (!curr.isDiscount) {
        const calculatedEarn = calculateDiscount({
          dpeWeightedAvg: curr.dpeWeightedAvg,
          invoiceAmount: curr.invoiceAmount,
          isDiscount: curr.isDiscount,
          rate: curr.rate,
        });

        return {
          invoiceAmount: acc.invoiceAmount + curr.invoiceAmount,
          earn: acc.earn + calculatedEarn,
        };
      }

      return {
        invoiceAmount: acc.invoiceAmount + curr.invoiceAmount,
        earn: acc.earn + curr.invoiceAmount * ((curr.rate ?? 0) / 100),
      };
    },
    {
      invoiceAmount: 0,
      earn: 0,
    }
  );

  return handleNaN(earn / invoiceAmount);
};

export const calculateBlendedRate = ({
  items,
  rateType,
}: {
  items: {
    dpeWeightedAvg: number;
    dpeWeightedSum: number;
    invoiceAmount: number;
    isDiscount: boolean;
    rate: number;
  }[];
  rateType: 'APR' | 'DISC';
}) => {
  if (!items.length) {
    return 0;
  }

  if (rateType === 'DISC') {
    return calculateBlendedDiscount({ items });
  }

  return calculateAverageApr({ items });
};

export const buildTakerMarketKey = (takerMarket: Pick<TakerMarket, 'marketUuid' | 'takerDivisionUuid'>) =>
  `${takerMarket.marketUuid}-${takerMarket.takerDivisionUuid}`;

// helper to create a rate map with the same rate info for all taker markets
export const createUniformRateMap = (takerMarkets: TakerMarket[], rateInfo: RateInfo | undefined): RateMap =>
  takerMarkets.reduce((map, tm) => {
    map[buildTakerMarketKey(tm)] = rateInfo;
    return map;
  }, {} as RateMap);

// helper to create a rate map with uniform rate/rateType but per-market pricing information
export const createMixedRateMap = (
  takerMarkets: TakerMarket[],
  uniformRate: number,
  uniformRateType: RateType,
  pricingInformationMap?: PricingInformationMap
): RateMap =>
  takerMarkets.reduce((map, tm) => {
    const marketKey = buildTakerMarketKey(tm);
    const pricingInfo = pricingInformationMap?.[marketKey];

    map[marketKey] = {
      rate: uniformRate,
      rateType: uniformRateType,
      pricingRateUuid: pricingInfo?.pricingRateUuid,
      suggestedRate: pricingInfo?.suggestedRate,
      suggestRateType: pricingInfo?.suggestRateType,
      customRate: uniformRate,
      customRateType: uniformRateType,
      // TODO: does this make sense here?
      pricingStrategy: undefined,
    };
    return map;
  }, {} as RateMap);

// builds payload for UPDATE_WORKING_TAKER_MARKETS_RATE action
// expects a rate map with rate info per takerMarket
export const buildUpdateWorkingTakerMarketsRatePayload = (
  takerMarkets: TakerMarket[],
  rateMap: RateMap
): {
  takerMarketKey: string;
  rateInfo: RateInfo | undefined;
}[] =>
  takerMarkets.map((tm) => {
    const takerMarketKey = buildTakerMarketKey(tm);
    return {
      takerMarketKey,
      rateInfo: rateMap[takerMarketKey],
    };
  });

// creates a workingTakerMarket from a takerMarket
export const createWorkingTakerMarket = (takerMarket: TakerMarket, rateInfo?: RateInfo): WorkingTakerMarket => ({
  currency: takerMarket.currency,
  makerOrganizationUuid: takerMarket.makerOrganizationUuid,
  marketUuid: takerMarket.marketUuid,
  takerDivisionUuid: takerMarket.takerDivisionUuid,
  rate: rateInfo?.rate,
  rateType: rateInfo?.rateType,
  pricingRateUuid: rateInfo?.pricingRateUuid,
  suggestedRate: rateInfo?.suggestedRate,
  suggestRateType: rateInfo?.suggestRateType,
  pricingStrategy: rateInfo?.pricingStrategy,
});

// gets a workingTakerMarket
export const getWorkingTakerMarket = (
  workingTakerMarkets: Record<string, WorkingTakerMarket>,
  takerMarketKey: string
): WorkingTakerMarket | undefined => workingTakerMarkets[takerMarketKey];

// ensures workingTakerMarket entries exist for a list of takerMarkets
export const ensureWorkingTakerMarkets = (
  existingWorkingMarkets: Record<string, WorkingTakerMarket>,
  takerMarkets: TakerMarket[]
): Record<string, WorkingTakerMarket> => {
  const result = { ...existingWorkingMarkets };

  takerMarkets.forEach((takerMarket) => {
    const key = buildTakerMarketKey(takerMarket);

    if (!result[key]) {
      result[key] = createWorkingTakerMarket(takerMarket, undefined);
    }
  });

  return result;
};

// resets rates for name your rate workingTakerMarket
export const resetRatesForWorkingTakerMarkets = (
  workingTakerMarkets: Record<string, WorkingTakerMarket>,
  selectedMarkets: TakerMarket[]
): Record<string, WorkingTakerMarket> => {
  const nameYourRateMarkets = getNameYourRateMarkets(selectedMarkets);
  const rateMap = createUniformRateMap(nameYourRateMarkets, undefined);

  return Object.entries(rateMap).reduce(
    (acc, [takerMarketKey]) => {
      const existingWorkingMarket = getWorkingTakerMarket(workingTakerMarkets, takerMarketKey);

      if (existingWorkingMarket) {
        // when a custom rate exists and pricingStrategy is nameYourRate, keep the custom rate as the active rate
        const shouldKeepCustomRate =
          existingWorkingMarket.pricingStrategy === 'nameYourRate' &&
          existingWorkingMarket.customRate !== undefined &&
          existingWorkingMarket.customRateType !== undefined;

        acc[takerMarketKey] = {
          ...existingWorkingMarket,
          rate: shouldKeepCustomRate ? existingWorkingMarket.customRate : undefined,
          rateType: shouldKeepCustomRate ? existingWorkingMarket.customRateType : undefined,
          pricingRateUuid: undefined,
          suggestedRate: undefined,
          suggestRateType: undefined,
          customRate: shouldKeepCustomRate ? existingWorkingMarket.customRate : undefined,
          customRateType: shouldKeepCustomRate ? existingWorkingMarket.customRateType : undefined,
          pricingStrategy: shouldKeepCustomRate ? existingWorkingMarket.pricingStrategy : undefined,
        };
      }

      return acc;
    },
    { ...workingTakerMarkets }
  );
};

// filters working taker markets down to the given selected set, preserving all rate state as-is.
// used when deselecting markets from the review screen and during background hydration (HYDRATE_SELECTED).
export const filterBySelectedTakerMarkets = (
  workingTakerMarkets: Record<string, WorkingTakerMarket>,
  remainingSelected: TakerMarket[]
): Record<string, WorkingTakerMarket> => {
  const remainingKeys = new Set(remainingSelected.map(buildTakerMarketKey));
  return Object.fromEntries(Object.entries(workingTakerMarkets).filter(([key]) => remainingKeys.has(key)));
};

// computes the next workingTakerMarkets after a toggle:
// removing — preserves rates for remaining markets unchanged
// adding — clears any suggested-rate selection for PRICE_DISCOVERY markets so the user must re-enter; BENCHMARK/STATIC markets are left as-is since their rates are not user-entered
export const computeWorkingTakerMarketsAfterToggle = (
  workingTakerMarkets: Record<string, WorkingTakerMarket>,
  nextSelected: TakerMarket[],
  isRemoving: boolean
): Record<string, WorkingTakerMarket> => {
  if (isRemoving) {
    return filterBySelectedTakerMarkets(workingTakerMarkets, nextSelected);
  }

  const selectedKeys = new Set(nextSelected.map(buildTakerMarketKey));
  const filtered = Object.fromEntries(Object.entries(workingTakerMarkets).filter(([key]) => selectedKeys.has(key)));
  return resetRatesForWorkingTakerMarkets(ensureWorkingTakerMarkets(filtered, nextSelected), nextSelected);
};

// updates working taker markets with rates from a rate map
export const updateWorkingTakerMarketsWithRates = (
  workingTakerMarkets: Record<string, WorkingTakerMarket>,
  rateMap: RateMap
): Record<string, WorkingTakerMarket> => {
  return Object.entries(rateMap).reduce(
    (acc, [takerMarketKey, rateInfo]) => {
      const existingWorkingMarket = getWorkingTakerMarket(workingTakerMarkets, takerMarketKey);

      if (existingWorkingMarket) {
        acc[takerMarketKey] = {
          ...existingWorkingMarket,
          rate: rateInfo?.rate,
          rateType: rateInfo?.rateType,
          pricingRateUuid: rateInfo?.pricingRateUuid,
          suggestedRate: rateInfo?.suggestedRate,
          suggestRateType: rateInfo?.suggestRateType,
          customRate: rateInfo?.customRate,
          customRateType: rateInfo?.customRateType,
          pricingStrategy: rateInfo?.pricingStrategy,
          isIndividuallyOverridden: rateInfo?.isIndividuallyOverridden,
          projectedClearingPercentage: rateInfo?.projectedClearingPercentage,
        };
      }

      return acc;
    },
    { ...workingTakerMarkets }
  );
};

// checks if all taker markets have defined rates
export const allMarketsHaveRates = (
  takerMarkets: TakerMarket[],
  getEffectiveRateFn: (tm: TakerMarket) => { rate: number | undefined; rateType: 'APR' | 'DISC' | undefined }
): boolean => {
  return takerMarkets.every((tm) => {
    const { rate } = getEffectiveRateFn(tm);
    return rate !== undefined && rate !== null;
  });
};

export const useRateTypeOptions = () => {
  const { t } = useTranslation();

  return [
    { value: 'APR', label: t('core.apr'), desc: t('offer.recurringMessage') },
    { value: 'DISC', label: t('core.discount'), desc: t('offer.flatRateMessage') },
  ];
};

export const hasNameYourRate = (takerMarkets: TakerMarket[]) =>
  takerMarkets.some((takerMarket) => takerMarket.marketPricingType === 'PRICE_DISCOVERY');

export const isNameYourRateOffer = (takerMarkets: TakerMarket[]) =>
  takerMarkets.every((takerMarket) => takerMarket.marketPricingType === 'PRICE_DISCOVERY');

export const getNameYourRateMarkets = (takerMarkets: TakerMarket[]) =>
  takerMarkets.filter((takerMarket) => takerMarket.marketPricingType === 'PRICE_DISCOVERY');

/**
 * Gets the existing rate from a taker market's config that matches the selected rate type
 * Returns null if no matching rate exists
 */
export const getExistingRateFromConfig = (
  takerMarket: TakerMarket,
  selectedRateType: RateType
): { rate: number; rateType: RateType } | null => {
  const configRate = takerMarket.offerConfig.maxApr ?? takerMarket.offerConfig.maxDiscount;
  const configRateType = takerMarket.offerConfig.isDiscountBidding ? 'DISC' : 'APR';
  const hasMatchingRate = configRate !== undefined && configRate !== null && configRateType === selectedRateType;

  return hasMatchingRate ? { rate: configRate, rateType: configRateType } : null;
};

export const getNameYourRateMarketsCount = (takerMarkets: TakerMarket[]) => {
  const nameYourRateMarkets = getNameYourRateMarkets(takerMarkets);

  return {
    count: nameYourRateMarkets.length,
    total: takerMarkets.length,
  };
};

export const getMarketPricingTypeScenario = (takerMarkets: TakerMarket[]) => {
  const uniqueMarketPricingTypes = Array.from(
    new Set(takerMarkets.map((takerMarket) => takerMarket.marketPricingType))
  );

  if (uniqueMarketPricingTypes.length === 1) {
    return 'single';
  }

  const hasPriceDiscovery = uniqueMarketPricingTypes.includes('PRICE_DISCOVERY');
  const hasBenchmarkOrStatic =
    uniqueMarketPricingTypes.includes('BENCHMARK') || uniqueMarketPricingTypes.includes('STATIC');

  // if both price discovery and benchmark/static are present, it's a mixed scenario
  if (hasPriceDiscovery && hasBenchmarkOrStatic) {
    return 'mixed';
  }

  return 'predetermined';
};

export interface ValidateRateResult {
  valid: boolean;
  errorMessage?: string;
}

export interface ValidateAndNormalizeRateResult {
  success: boolean;
  normalizedRate?: number;
  errorMessage?: string;
}

export const useValidateRate = () => {
  const { t } = useTranslation();

  const validateRate = (rateInput: string, rateTypeInput: RateType): ValidateRateResult => {
    const rate = getNormalizedRate(rateInput);

    // not a valid rate
    if (rateInput === '') {
      return {
        valid: false,
        errorMessage: t('offerSubmitDialog.errors.enterAValidRate'),
      };
    }

    if (rateTypeInput === 'DISC') {
      const minThreshold = 0;
      const maxRate = 30;

      const isRateValid = isValidRateInput(rateInput) && rate > minThreshold && rate <= maxRate;

      // rate must be greater than zero
      if (rate <= minThreshold) {
        return {
          valid: false,
          errorMessage: t('offerSubmitDialog.errors.enterRateGreaterThanZero'),
        };
      }

      // rate must be less than or equal to 30
      if (rate > maxRate) {
        return {
          valid: false,
          errorMessage: t('offerSubmitDialog.errors.discRateCannotBeGreaterThanMax', { maxRate }),
        };
      }

      // fall back if entering an invalid rate like '...'
      if (!isRateValid) {
        return {
          valid: false,
          errorMessage: t('offerSubmitDialog.errors.enterAValidRate'),
        };
      }

      return {
        valid: isRateValid,
        errorMessage: undefined,
      };
    }

    if (rateTypeInput === 'APR') {
      const minThreshold = 0;
      const maxRate = 99;

      const isRateValid = isValidRateInput(rateInput) && rate > minThreshold && rate <= maxRate;

      // rate must be greater than zero
      if (rate <= minThreshold) {
        return {
          valid: false,
          errorMessage: t('offerSubmitDialog.errors.enterRateGreaterThanZero'),
        };
      }

      // rate must be less than or equal to 99
      if (rate > maxRate) {
        return {
          valid: false,
          errorMessage: t('offerSubmitDialog.errors.aprRateCannotBeGreaterThanMax', { maxRate }),
        };
      }

      // fall back if entering an invalid rate like '...'
      if (!isRateValid) {
        return {
          valid: false,
          errorMessage: t('offerSubmitDialog.errors.enterAValidRate'),
        };
      }

      return {
        valid: isRateValid,
        errorMessage: undefined,
      };
    }

    return {
      valid: false,
      errorMessage: t('offerSubmitDialog.errors.enterAValidRate'),
    };
  };

  return { validateRate };
};

/**
 * Validates and normalizes a rate input string
 * Returns success with normalized rate, or error message if validation fails
 */
export const validateAndNormalizeRate = (
  rateInput: string,
  rateType: RateType,
  validateRateFn: (rateInput: string, rateType: RateType) => ValidateRateResult,
  t: (key: string) => string
): ValidateAndNormalizeRateResult => {
  if (!rateInput || !rateType) {
    return {
      success: false,
      errorMessage: t('builder.bulkRateRequired'),
    };
  }

  const domainRate = getNormalizedRate(rateInput).toString();
  if (!domainRate || domainRate === '0') {
    return {
      success: false,
      errorMessage: t('builder.bulkRateRequired'),
    };
  }

  const validationResult = validateRateFn(domainRate, rateType);
  if (!validationResult.valid) {
    return {
      success: false,
      errorMessage: validationResult.errorMessage,
    };
  }

  const normalizedRate = getNormalizedRate(domainRate);

  return {
    success: true,
    normalizedRate,
  };
};

export const PRICING_RATE_TYPE_MAP = {
  APR: 'apr',
  DISC: 'discount',
} as const;

/**
 * Gets the rate key for pricing data based on rate type
 */
export const getRateKey = (rateType: RateType): 'apr' | 'discount' => {
  return rateType === 'DISC' ? 'discount' : 'apr';
};

/**
 * Gets the frequency key for pricing data based on offer builder frequency
 */
export const getFrequencyKey = (frequency: OfferBuilderFrequency | null): 'oneTime' | 'recurring' => {
  return frequency === 'oneTime' ? 'oneTime' : 'recurring';
};

export const getMarketsWithZeroEligibleAmount = (takerMarkets: TakerMarket[]) =>
  takerMarkets.filter((market) => market.eligibleInvoiceAmount === 0);

export const getMarketsWithPositiveEligibleAmount = (takerMarkets: TakerMarket[]) =>
  takerMarkets.filter((market) => market.eligibleInvoiceAmount > 0);

export const hasAllMarketsWithZeroEligibleAmount = (takerMarkets: TakerMarket[]) =>
  takerMarkets.length > 0 && takerMarkets.every((market) => market.eligibleInvoiceAmount === 0);

export const getMixedEligibleAmountInfo = (takerMarkets: TakerMarket[]) => {
  const zeroEligibleCount = getMarketsWithZeroEligibleAmount(takerMarkets).length;
  const nonZeroEligibleCount = getMarketsWithPositiveEligibleAmount(takerMarkets).length;
  const totalCount = takerMarkets.length;

  const hasMixedEligibleAmounts = zeroEligibleCount > 0 && nonZeroEligibleCount > 0;

  return {
    hasMixedEligibleAmounts,
    zeroEligibleCount,
    nonZeroEligibleCount,
    totalCount,
  };
};

// calculates rate map from pricing data for specific strategy and rate type
export const calculateRateMapFromPricingData = (
  nameYourRateMarkets: TakerMarket[],
  pricingData: CombinedPricingSuggestion | undefined,
  strategy: OfferBuilderPricingStrategy,
  rateType: RateType,
  frequency: OfferBuilderFrequency | null
): Record<string, RateInfo | undefined> => {
  const rateKey = rateType === 'APR' ? 'apr' : 'discount';

  return nameYourRateMarkets.reduce<Record<string, RateInfo | undefined>>((map, takerMarket) => {
    const rates = pricingData?.supplierMarkets.find(
      (supplierMarket) =>
        supplierMarket.marketUuid === takerMarket.marketUuid &&
        supplierMarket.supplierDivisionUuid === takerMarket.takerDivisionUuid
    );

    let pricingRateUuid: string | undefined;
    let trendingRate: number | undefined;
    const takerMarketKey = buildTakerMarketKey(takerMarket);

    if (strategy === 'expressAccept') {
      pricingRateUuid = rates?.[rateKey]?.expressAccept?.pricingRateUuid;
      trendingRate = rates?.[rateKey]?.expressAccept?.trendingRate;
    } else {
      pricingRateUuid = rates?.[rateKey]?.[frequency ?? 'recurring']?.pricingRateUuid;
      trendingRate = rates?.[rateKey]?.[frequency ?? 'recurring']?.trendingRate;
    }

    map[takerMarketKey] =
      pricingRateUuid !== undefined && trendingRate !== undefined
        ? {
            pricingRateUuid,
            rate: trendingRate,
            rateType,
            suggestedRate: trendingRate,
            suggestRateType: rateType,
          }
        : undefined;

    return map;
  }, {});
};

/**
 * Checks if a taker market has an Express Accept offer option configured
 */
export const hasExpressAcceptOfferOption = (takerMarket: TakerMarket): boolean => {
  const { offerConfig, offerOptions } = takerMarket;
  // no offer exists or current offer is inactive
  const isNewOffer = !offerConfig.uuid || offerConfig.status === 'inactive';
  const targetOptions = isNewOffer ? offerOptions?.newOffer : offerOptions?.availableOffer;

  return (
    targetOptions?.some(
      (option) => option.frequency === OfferOptionFrequency.ONE_TIME && option.type === OfferOptionType.EXPRESS_ACCEPT
    ) ?? false
  );
};

/**
 * Checks whether a single taker market is eligible to build a new standard offer on.
 * Excludes preferred-term offers, disabled markets, manually-awarded offers, and active
 * Express Accept offers (which can have zero remaining invoices but are still active).
 */
export const isTakerMarketOfferable = (takerMarket: TakerMarket): boolean =>
  takerMarket.offerConfig.offerType !== 'PREFERRED_TERM' &&
  takerMarket.marketIsEnabled &&
  !takerMarket.offerConfig.isManuallyAwarded &&
  !takerMarket.offerConfig.isActiveExpressAccept;

export const isExpressAcceptEligible = (takerMarkets: TakerMarket[]): boolean => {
  return takerMarkets.every((takerMarket) => {
    // must have offer options and a next clear time
    return hasExpressAcceptOfferOption(takerMarket) && !!takerMarket.marketNextClearTime;
  });
};

/**
 * Checks if a taker market has a one-time price discovery offer option configured
 */
export const hasPriceDiscoveryOneTimeOfferOption = (takerMarket: TakerMarket): boolean => {
  const { offerConfig, offerOptions } = takerMarket;
  // Reason: no offer exists or current offer is inactive → use the 'newOffer' subset
  const isNewOffer = !offerConfig.uuid || offerConfig.status === 'inactive';
  const targetOptions = isNewOffer ? offerOptions?.newOffer : offerOptions?.availableOffer;

  return (
    targetOptions?.some(
      (option) => option.frequency === OfferOptionFrequency.ONE_TIME && option.type === OfferOptionType.PRICE_DISCOVERY
    ) ?? false
  );
};

export const isPriceDiscoveryOneTimeEligible = (takerMarkets: TakerMarket[]): boolean => {
  return takerMarkets.every((takerMarket) => {
    return hasPriceDiscoveryOneTimeOfferOption(takerMarket) && !!takerMarket.marketNextClearTime;
  });
};

export type IsInstantPayEligible = (takerMarket: TakerMarket) => boolean;

/**
 * Checks if a specific taker market has Express Accept available
 * Requires: eligible amount > 0, express accept offer option, next clear time, and pricing data
 */
export const hasExpressAcceptForMarket = (
  takerMarket: TakerMarket,
  pricingData: CombinedPricingSuggestion | undefined,
  rateType: RateType,
  isInstantPayEligible?: IsInstantPayEligible
): boolean => {
  if (isInstantPayEligible?.(takerMarket)) {
    return false;
  }

  // Must have eligible invoice amount
  if (takerMarket.eligibleInvoiceAmount <= 0) {
    return false;
  }

  // Must have offer options and a next clear time
  if (!hasExpressAcceptOfferOption(takerMarket) || !takerMarket.marketNextClearTime) {
    return false;
  }

  // Check pricing data for Express Accept rate
  if (!pricingData) {
    return false;
  }

  const rateKey = getRateKey(rateType);
  const marketPricing = pricingData.supplierMarkets.find(
    (p) => p.marketUuid === takerMarket.marketUuid && p.supplierDivisionUuid === takerMarket.takerDivisionUuid
  );

  const expressAcceptRate = marketPricing?.[rateKey]?.['expressAccept'];
  return expressAcceptRate?.trendingRate !== undefined && expressAcceptRate?.trendingRate !== null;
};

/**
 * Checks if a specific taker market has Trending Rate available
 * Requires: eligible amount > 0 and pricing data with trending rate
 */
export const hasTrendingRateForMarket = (
  takerMarket: TakerMarket,
  pricingData: CombinedPricingSuggestion | undefined,
  rateType: RateType,
  frequency: OfferBuilderFrequency | null
): boolean => {
  // Must have eligible invoice amount
  if (takerMarket.eligibleInvoiceAmount <= 0) {
    return false;
  }

  // Check pricing data for trending rate
  if (!pricingData) {
    return false;
  }

  const rateKey = getRateKey(rateType);
  const frequencyKey = getFrequencyKey(frequency);

  const marketPricing = pricingData.supplierMarkets.find(
    (p) => p.marketUuid === takerMarket.marketUuid && p.supplierDivisionUuid === takerMarket.takerDivisionUuid
  );

  const trendingRate = marketPricing?.[rateKey]?.[frequencyKey];
  return trendingRate?.trendingRate !== undefined && trendingRate?.trendingRate !== null;
};

export interface PricingOption {
  type: 'trendingRate' | 'expressAccept';
  rate: number;
  rateType: RateType;
  pricingRateUuid?: string;
  suggestedRateStart?: number;
  suggestedRateEnd?: number;
}

/**
 * Builds the list of selectable pricing options (trending rate, and express accept when
 * eligible) for a single taker market. Shared by the offer builder and the edit rate modal.
 * Note: the edit rate modal filters out express accept before rendering — see
 * EditRateModal/PricingStrategyOptions.tsx.
 */
export const buildPricingOptionsForMarket = ({
  pricingData,
  takerMarket,
  rateType,
  frequency,
  isInstantPayEligible,
}: {
  pricingData: CombinedPricingSuggestion | null | undefined;
  takerMarket: TakerMarket;
  rateType: RateType;
  frequency: OfferBuilderFrequency | null;
  isInstantPayEligible?: IsInstantPayEligible;
}): PricingOption[] => {
  if (!pricingData || takerMarket.eligibleInvoiceAmount <= 0) {
    return [];
  }

  const rateKey = getRateKey(rateType);
  const frequencyKey = getFrequencyKey(frequency);
  const marketPricing = pricingData.supplierMarkets.find(
    (tm) => tm.marketUuid === takerMarket.marketUuid && tm.supplierDivisionUuid === takerMarket.takerDivisionUuid
  );

  if (!marketPricing) {
    return [];
  }

  const rates: Array<{
    type: PricingOption['type'];
    value: (typeof marketPricing)[typeof rateKey][typeof frequencyKey];
  }> = [{ type: 'trendingRate', value: marketPricing[rateKey][frequencyKey] }];

  if (
    frequencyKey === 'oneTime' &&
    hasExpressAcceptForMarket(takerMarket, pricingData, rateType, isInstantPayEligible)
  ) {
    rates.unshift({ type: 'expressAccept', value: marketPricing[rateKey]['expressAccept'] });
  }

  return rates
    .map((rate): PricingOption | null => {
      const trendingRate = rate.value?.trendingRate;
      if (trendingRate === undefined || !Number.isFinite(trendingRate)) {
        return null;
      }

      return {
        type: rate.type,
        rate: trendingRate,
        rateType,
        pricingRateUuid: rate.value.pricingRateUuid,
        suggestedRateStart: rate.value.trendingRateStart,
        suggestedRateEnd: rate.value.trendingRateEnd,
      };
    })
    .filter((option): option is PricingOption => option !== null);
};

export const setTakerMarketError =
  (dispatch: React.Dispatch<OfferBuilderAction>) => (takerMarketKey: string, errorType: 'NO_RATE_SELECTED') => {
    dispatch({
      type: 'SET_TAKER_MARKET_ERRORS',
      payload: {
        takerMarketKey,
        errors: [{ takerMarketKey, type: errorType }],
      },
    });
  };

export const getTakerMarketErrors = (state: OfferBuilderState, takerMarketKey: string): TakerMarketError[] => {
  return state.takerMarketErrors[takerMarketKey] || [];
};

export const hasTakerMarketError = (
  state: OfferBuilderState,
  takerMarketKey: string,
  errorType?: 'NO_RATE_SELECTED'
): boolean => {
  const errors = getTakerMarketErrors(state, takerMarketKey);

  if (!errorType) {
    return errors.length > 0;
  }

  return errors.some((error) => error.type === errorType);
};

export const hasAnyTakerMarketError = (state: OfferBuilderState, errorType: 'NO_RATE_SELECTED'): boolean => {
  return Object.values(state.takerMarketErrors).some((errors) => errors.some((error) => error.type === errorType));
};

export const clearTakerMarketErrorsForKeys = (
  currentTakerMarketErrors: Record<string, TakerMarketError[]>,
  takerMarketKeys: string[],
  errorType?: 'NO_RATE_SELECTED'
): Record<string, TakerMarketError[]> => {
  let updatedErrors = { ...currentTakerMarketErrors };

  takerMarketKeys.forEach((takerMarketKey) => {
    const currentErrors = updatedErrors[takerMarketKey] || [];

    if (errorType) {
      // clear specific error type
      const filteredErrors = currentErrors.filter((error) => error.type !== errorType);

      if (filteredErrors.length === 0) {
        // remove the key if no errors remain
        const { [takerMarketKey]: _, ...rest } = updatedErrors;
        updatedErrors = rest;
      } else {
        updatedErrors[takerMarketKey] = filteredErrors;
      }
    } else {
      // clear all errors for this taker market
      const { [takerMarketKey]: _, ...rest } = updatedErrors;
      updatedErrors = rest;
    }
  });

  return updatedErrors;
};

/**
 * Creates a rate info object for a specific strategy
 * Returns undefined if the strategy is not available for the market
 */
export const createRateInfoForStrategy = (
  takerMarket: TakerMarket,
  strategy: 'expressAccept' | 'trendingRate' | 'existingRate' | 'nameYourRate',
  rateType: RateType,
  frequency: OfferBuilderFrequency | null,
  pricingData: CombinedPricingSuggestion | undefined,
  customRate?: number,
  existingCustomRate?: number,
  existingCustomRateType?: RateType,
  isInstantPayEligible?: IsInstantPayEligible
): RateInfo | undefined => {
  const rateKey = getRateKey(rateType);
  const frequencyKey = getFrequencyKey(frequency);

  const marketPricing = pricingData?.supplierMarkets.find(
    (p) => p.marketUuid === takerMarket.marketUuid && p.supplierDivisionUuid === takerMarket.takerDivisionUuid
  );

  switch (strategy) {
    case 'expressAccept': {
      if (!hasExpressAcceptForMarket(takerMarket, pricingData, rateType, isInstantPayEligible)) {
        return undefined;
      }

      const expressAcceptRate = marketPricing?.[rateKey]?.['expressAccept'];
      if (!expressAcceptRate?.trendingRate) {
        return undefined;
      }

      return {
        rate: expressAcceptRate.trendingRate,
        rateType,
        pricingRateUuid: expressAcceptRate.pricingRateUuid,
        suggestedRate: expressAcceptRate.trendingRate,
        suggestRateType: rateType,
        customRate: existingCustomRate,
        customRateType: existingCustomRateType,
        pricingStrategy: 'expressAccept',
        isIndividuallyOverridden: false,
      };
    }

    case 'trendingRate': {
      if (!hasTrendingRateForMarket(takerMarket, pricingData, rateType, frequency)) {
        return undefined;
      }

      const trendingRate = marketPricing?.[rateKey]?.[frequencyKey];
      if (!trendingRate?.trendingRate) {
        return undefined;
      }

      return {
        rate: trendingRate.trendingRate,
        rateType,
        pricingRateUuid: trendingRate.pricingRateUuid,
        suggestedRate: trendingRate.trendingRate,
        suggestRateType: rateType,
        customRate: existingCustomRate,
        customRateType: existingCustomRateType,
        pricingStrategy: 'trendingRate',
        isIndividuallyOverridden: false,
      };
    }

    case 'existingRate': {
      const existingConfigRate = getExistingRateFromConfig(takerMarket, rateType);
      if (!existingConfigRate) {
        return undefined;
      }

      return {
        rate: existingConfigRate.rate,
        rateType: existingConfigRate.rateType,
        pricingRateUuid: undefined,
        suggestedRate: undefined,
        suggestRateType: undefined,
        customRate: existingConfigRate.rate,
        customRateType: existingConfigRate.rateType,
        pricingStrategy: 'nameYourRate',
        isIndividuallyOverridden: false,
      };
    }

    case 'nameYourRate': {
      if (customRate === undefined) {
        return undefined;
      }

      return {
        rate: customRate,
        rateType,
        pricingRateUuid: undefined,
        suggestedRate: undefined,
        suggestRateType: undefined,
        customRate,
        customRateType: rateType,
        pricingStrategy: 'nameYourRate',
        isIndividuallyOverridden: false,
      };
    }

    default:
      return undefined;
  }
};

/**
 * Gets the localized title for a pricing strategy
 */
export const getStrategyTitle = (
  strategy: 'expressAccept' | 'trendingRate' | 'existingRate' | 'nameYourRate',
  t: (key: string) => string
): string => {
  switch (strategy) {
    case 'expressAccept':
      return t('core.expressAccept');
    case 'trendingRate':
      return t('core.trending');
    case 'existingRate':
      return t('builder.existingRate');
    case 'nameYourRate':
      return t('core.nameYourRate');
    default:
      return '';
  }
};

/**
 * Builds a payload for updating working taker markets with rate info
 * Filters out markets where rate info cannot be created
 */
export const buildBulkRatePayload = (
  takerMarkets: TakerMarket[],
  strategy: 'expressAccept' | 'trendingRate' | 'existingRate' | 'nameYourRate',
  rateType: RateType,
  frequency: OfferBuilderFrequency | null,
  pricingData: CombinedPricingSuggestion | undefined,
  workingTakerMarkets: Record<string, WorkingTakerMarket>,
  customRate?: number,
  skipMarketsWithZeroEligibleAmount?: boolean,
  isInstantPayEligible?: IsInstantPayEligible
): { takerMarketKey: string; rateInfo: RateInfo }[] => {
  return takerMarkets
    .map((tm) => {
      const marketKey = buildTakerMarketKey(tm);
      const existingMarket = workingTakerMarkets[marketKey];

      // For Express Accept and Trending Rate, optionally skip divisions with no eligible amount
      if (skipMarketsWithZeroEligibleAmount && strategy !== 'existingRate' && tm.eligibleInvoiceAmount <= 0) {
        return null;
      }

      const rateInfo = createRateInfoForStrategy(
        tm,
        strategy,
        rateType,
        frequency,
        pricingData,
        customRate,
        existingMarket?.customRate,
        existingMarket?.customRateType,
        isInstantPayEligible
      );

      if (!rateInfo) {
        return null;
      }

      return {
        takerMarketKey: marketKey,
        rateInfo,
      };
    })
    .filter((item): item is NonNullable<typeof item> => item !== null);
};

export const useGetLocationFromPathname = (): OfferBuilderLocation | undefined => {
  const { pathname } = useLocation();

  // if location contains 'homepage' return 'homepage'
  if (pathname.includes('homepage')) {
    return 'homepage';
  }

  // if location contains 'offer-activity' return 'offer-activity'
  if (pathname.includes('offer-activity')) {
    return 'offer-activity';
  }

  // if location ends with 'customers' return 'customers'
  if (pathname.endsWith('customers')) {
    return 'customers';
  }

  // if location ends with 'markets' return 'markets'
  if (pathname.endsWith('markets')) {
    return 'markets';
  }

  // if location contains 'customers/*/markets/*/division/*' return 'division'
  if (/customers\/[^/]+\/markets\/[^/]+\/division\/[^/]+/.test(pathname)) {
    return 'division';
  }

  // else return undefined
  return undefined;
};
